/**
 * Global Logger singleton.
 *
 * Provides a single debug flag for the entire app.
 * Toggle debug mode with Shift+L from any scene, or call
 * `Logger.getInstance().toggle()` programmatically.
 *
 * Usage:
 *   import { Logger } from '../utils/Logger'
 *   const logger = Logger.getInstance()
 *   logger.log('something happened', value)
 */
export class Logger {
  private static instance: Logger
  private _debugMode: boolean = false
  private _listeners: Array<(enabled: boolean) => void> = []

  private constructor() {}

  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger()
    }
    return Logger.instance
  }

  /** Enable debug logging. */
  public enable(): void {
    this._debugMode = true
    this._notify()
  }

  /** Disable debug logging. */
  public disable(): void {
    this._debugMode = false
    this._notify()
  }

  /** Toggle debug logging. Returns the new state. */
  public toggle(): boolean {
    this._debugMode = !this._debugMode
    this._notify()
    return this._debugMode
  }

  /** Returns true when debug logging is active. */
  public isEnabled(): boolean {
    return this._debugMode
  }

  /**
   * Log to the console only when debug mode is on.
   * Signature is intentionally permissive (`any[]`) to mirror `console.log`.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public log(...args: any[]): void {
    if (this._debugMode) {
      console.log(...args)
    }
  }

  /**
   * Register a callback that fires whenever debug mode changes.
   * Returns an unsubscribe function.
   */
  public onToggle(callback: (enabled: boolean) => void): () => void {
    this._listeners.push(callback)
    return () => {
      this._listeners = this._listeners.filter((l) => l !== callback)
    }
  }

  private _notify(): void {
    const state = this._debugMode
    this._listeners.forEach((l) => l(state))
  }
}
